import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ShirtSize,
  getShirtSizeDisplay,
  getAllShirtSizes,
} from "@/types/shirtSizes";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import {
  MobileForm,
  MobileFormField,
  MobileFormActions,
} from "@/components/ui/mobile-form";
import axios from "axios";
import InputMask from "react-input-mask";

const formSchema = z
  .object({
    firstName: z.string().min(3, "First name must be at least 2 characters"),
    lastName: z.string().min(3, "Last name must be at least 2 characters"),
    email: z.string().email("Invalid email address"),
    phoneNumber: z.string().min(10, "Please enter a valid phone number"),
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string(),
    howDidYouHearAboutUs: z
      .string()
      .min(1, "Please tell us how you heard about us"),
    shirtSize: z.nativeEnum(ShirtSize, {
      required_error: "Please select a shirt size",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

interface SignUpProps {
  isEmployeeForm?: boolean;
}

export default function SignUp({ isEmployeeForm = false }: SignUpProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [emailConsent, setEmailConsent] = useState(false);
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      howDidYouHearAboutUs: "",
      shirtSize: undefined,
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      const route = process.env.REACT_APP_BASE_API_URL + "auth/signup";

      const signUpData = {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        email: values.email,
        password: values.password,
        howDidYouHearAboutUs: values.howDidYouHearAboutUs,
        shirtSize: values.shirtSize,
        smsConsent,
        emailConsent,
      };

      await axios.post(route, signUpData);

      toast({
        title: isEmployeeForm
          ? "Employee added successfully"
          : "Registration successful! Welcome aboard!",
        description: isEmployeeForm
          ? "The new employee can now log in"
          : "You'll be redirected to learn about next steps",
        variant: "success",
      });

      if (!isEmployeeForm) {
        setTimeout(() => {
          navigate("/registration-success");
        }, 1500);
      }

      form.reset();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to create account. Please try again.";

      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={
        isEmployeeForm
          ? ""
          : "min-h-screen flex items-center justify-center p-4 sm:p-6"
      }
    >
      <div className="w-full max-w-md space-y-6 sm:space-y-8">
        {!isEmployeeForm && (
          <div className="text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
              Create Account
            </h2>
            <p className="mt-2 text-foreground">Sign up for a new account</p>
          </div>
        )}

        <Form {...form}>
          <MobileForm
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 sm:space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <MobileFormField>
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </MobileFormField>

              <MobileFormField>
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </MobileFormField>
            </div>

            <MobileFormField>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </MobileFormField>

            <MobileFormField>
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <InputMask
                        mask="(999) 999-9999"
                        value={field.value}
                        onChange={field.onChange}
                      >
                        {(
                          inputProps: React.InputHTMLAttributes<HTMLInputElement>,
                        ) => <Input {...inputProps} />}
                      </InputMask>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </MobileFormField>

            <MobileFormField>
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </MobileFormField>

            <MobileFormField>
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </MobileFormField>

            <MobileFormField>
              <FormField
                control={form.control}
                name="howDidYouHearAboutUs"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>How did you hear about us?</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g. Friend, Social Media, etc."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </MobileFormField>

            <MobileFormField>
              <FormField
                control={form.control}
                name="shirtSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Shirt Size (For Company Uniform)</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || ""}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a size" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getAllShirtSizes().map((size) => (
                          <SelectItem key={size} value={size}>
                            {getShirtSizeDisplay(size)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </MobileFormField>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="smsConsent"
                  checked={smsConsent}
                  onCheckedChange={(checked) =>
                    setSmsConsent(checked as boolean)
                  }
                />
                <label
                  htmlFor="smsConsent"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I consent to receive SMS notifications
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="emailConsent"
                  checked={emailConsent}
                  onCheckedChange={(checked) =>
                    setEmailConsent(checked as boolean)
                  }
                />
                <label
                  htmlFor="emailConsent"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I consent to receive email notifications
                </label>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mt-2 text-xs text-muted-foreground">
                <span>
                  By signing up, you agree to our
                  <Link
                    to="/terms"
                    className="text-foreground underline font-semibold hover:text-primary ml-1"
                  >
                    Terms & Conditions
                  </Link>
                  and
                  <Link
                    to="/privacy"
                    className="text-foreground underline font-semibold hover:text-primary ml-1"
                  >
                    Privacy Policy
                  </Link>
                  .
                </span>
              </div>
            </div>

            <MobileFormActions>
              <Button type="submit" disabled={isLoading} className="w-full">
                {isLoading
                  ? "Creating Account..."
                  : isEmployeeForm
                    ? "Add Employee"
                    : "Create Account"}
              </Button>
            </MobileFormActions>
          </MobileForm>
        </Form>

        {!isEmployeeForm && (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link
                to="/signin"
                className="font-medium text-foreground hover:text-primary"
              >
                Sign in
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
